import api from './api';

/**
 * Artist Service - Handles all artist-related API calls
 */
const artistService = {
  /**
   * Get all artists for the current user
   * @returns {Promise} Resolves to a list of user's artists
   */
  getAll: async () => {
    try {
      const response = await api.get('api/user/artists');
      return response.data;
    } catch (error) {
      console.error('Error fetching artists:', error);
      throw error;
    }
  },

  /**
   * Get a specific artist by ID
   * @param {string} id - Artist ID
   * @returns {Promise} Resolves to artist details
   */
  get: async (id) => {
    try {
      const response = await api.get(`api/user/artists/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching artist ${id}:`, error);
      throw error;
    }
  },

  /**
   * Save an artist (create or update)
   * @param {Object} artist - Artist data
   * @returns {Promise} Resolves to saved artist
   */
  save: async (artist) => {
    const data = JSON.parse(JSON.stringify(artist));

    if (data._id) {
      // Update existing artist
      const id = data._id;

      // Remove fields that should not be included in update
      delete data._id;
      delete data.createdAt;
      delete data.updatedAt;

      const response = await api.put(`api/user/artists/${id}`, data);
      return response.data;
    } else {
      // Create new artist
      const response = await api.post('api/user/artists', data);
      return response.data;
    }
  },

  /**
   * Delete an artist
   * @param {string} id - Artist ID
   * @returns {Promise} Resolves to deletion result
   */
  remove: async (id) => {
    const response = await api.delete(`api/user/artists/${id}`);
    return response.data;
  },

  /**
   * Check if the artist name is already on Spotify and get the found Spotify profiles
   * @param {string} artist - Artist name to check
   * @param {boolean} urlSearch - Whether to use URL search format
   * @returns {Promise} Resolves to matching Spotify profiles
   */
  checkArtistName: async (artist, urlSearch) => {
    const response = await api.get('api/check-artist-name', {
      params: { artist, urlSearch },
    });
    return response.data;
  },

  /**
   * Link an artist to a Spotify profile
   * @param {string} artistId - Artist ID
   * @param {string} spotifyProfileId - Spotify profile ID to link
   * @returns {Promise} Resolves to updated artist with Spotify link
   */
  linkSpotifyProfile: async (artistId, spotifyProfileId) => {
    const response = await api.post(`api/user/artists/${artistId}/spotify-link`, {
      spotifyProfileId,
    });
    return response.data;
  },

  /**
   * Update artist's social media links
   * @param {string} artistId - Artist ID
   * @param {Object} socialLinks - Object containing social media links
   * @returns {Promise} Resolves to updated artist with social links
   */
  updateSocialLinks: async (artistId, socialLinks) => {
    const response = await api.put(`api/user/artists/${artistId}/social`, socialLinks);
    return response.data;
  },

  /**
   * Get Indiefy Me for an artist
   * @param {string} artistId - Artist ID
   * @returns {Promise} Resolves to Indiefy Me data
   */
  getIndiefyMe: async () => {
    const response = await api.get('api/user/indiefy-me');
    return response.data;
  },

  /**
   * Get an Indiefy Me page by ID
   * @param {string} pageId - Page ID
   * @returns {Promise} Resolves to Indiefy Me page data
   */
  getIndiefyMePage: async (pageId) => {
    const response = await api.get(`api/user/indiefy-me/${pageId}`);
    return response.data;
  },

  /**
   * Update an Indiefy Me page
   * @param {Object} page - Indiefy Me page data
   * @returns {Promise} Resolves to updated Indiefy Me page data
   */
  updatetIndiefyMePage: async (page) => {
    const pageId = page._id;
    delete page._id;
    const response = await api.put(`api/user/indiefy-me/${pageId}`, page);
    return response.data;
  },

  /**
   * Get Indiefy My Visits for an artist
   * @param {string} artistId - Artist ID
   * @returns {Promise} Resolves to Indiefy My Visits data
   */
  getIndiefyMyVisits: async (artistId) => {
    const response = await api.get(`api/user/me-visits/${artistId}`);
    return response.data;
  },

  /**
   * Get followers for an artist
   * @param {string} artistId - Artist ID
   * @returns {Promise} Resolves to followers data
   */
  getFollowers: async (artistId) => {
    const response = await api.get(`api/followers/${artistId}`);
    return response.data;
  },

  /**
   * Follow an artist
   * @param {string} artistId - Artist ID to follow
   * @returns {Promise} Resolves to follow confirmation
   * @param {Object} params - Parameters for the follow artist API call
   * @param {string} params.source - Source of the follow (e.g. 'catalog', 'release-follows', 'spotify-presave', 'release-presave')
   * @param {string} params.email - Email of the user following the artist
   * @param {string} params.artist - Artist ID of the artist following the artist
   */
  followArtist: async (artistId, params = {}) => {
    const response = await api.post(`api/follow-artist/${artistId}`, params);
    return response.data;
  },

  /**
   * Unfollow an artist
   * @param {string} artistId - Artist ID to unfollow
   * @returns {Promise} Resolves to unfollow confirmation
   */
  unfollowArtist: async (artistId, email) => {
    const response = await api.get(`api/unfollow-artist/${artistId}?email=${email}`);
    return response.data;
  },
};

export default artistService;